export interface InsufficientCreditsDetails {
  required_mt?: number;
  available_mt?: number;
  shortfall_mt?: number;
  action?: string;
  purchase_path?: string;
  recovery_path?: string;
  checkout_flow?: string;
}

export interface InsufficientCreditsPayload {
  code: 'billing_insufficient_credits';
  message: string;
  details: InsufficientCreditsDetails;
  purchasePath: string;
}

const DEFAULT_PURCHASE_PATH = '/billing?section=credits';

const normalizeCreditRecoveryPath = (path: string): string => {
  // Older deployed backends return the former combined purchase page. Within
  // an insufficient-credit error the intent is unambiguously to add credits.
  const [pathname, search = ''] = path.split('?');
  if (pathname !== '/billing') return path;
  const params = new URLSearchParams(search);
  if (!params.has('section') || params.get('section') === 'purchase') {
    params.set('section', 'credits');
  }
  return `${pathname}?${params}`;
};
const BLOCKING_PROMPT_RUN_ESTIMATE_ERROR_CODES = new Set([
  'validation_prompt_run_target_empty',
  'validation_media_duration_required',
  'validation_media_duration_probe_failed',
]);

const getStructuredError = (error: any): any => {
  const structured = error?.response?.data?.error;
  if (structured?.code) return structured;
  const legacyDetail = error?.response?.data?.detail;
  if (legacyDetail?.code) return legacyDetail;
  return null;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

export function extractInsufficientCredits(error: any): InsufficientCreditsPayload | null {
  const structured = getStructuredError(error);
  if (structured?.code !== 'billing_insufficient_credits') return null;

  const rawDetails = structured.details || {};
  const details: InsufficientCreditsDetails = {
    ...rawDetails,
    required_mt: toFiniteNumber(rawDetails.required_mt),
    available_mt: toFiniteNumber(rawDetails.available_mt),
    shortfall_mt: toFiniteNumber(rawDetails.shortfall_mt),
  };
  const purchasePath = (
    typeof details.purchase_path === 'string' && details.purchase_path.trim()
      ? details.purchase_path
      : typeof details.recovery_path === 'string' && details.recovery_path.trim()
        ? details.recovery_path
        : DEFAULT_PURCHASE_PATH
  );

  return {
    code: 'billing_insufficient_credits',
    message: structured.message || 'Insufficient credits for this operation.',
    details,
    purchasePath: normalizeCreditRecoveryPath(purchasePath),
  };
}

export function describeInsufficientCredits(payload: InsufficientCreditsPayload): string {
  const required = payload.details.required_mt;
  const available = payload.details.available_mt;
  const shortfall = payload.details.shortfall_mt;
  if (
    typeof required === 'number'
    && typeof available === 'number'
    && typeof shortfall === 'number'
  ) {
    return `Insufficient credits. Required ${required.toFixed(2)} MT, available ${available.toFixed(2)} MT, short by ${shortfall.toFixed(2)} MT.`;
  }
  return payload.message;
}

export function isBlockingPromptRunEstimateError(error: any): boolean {
  const structured = getStructuredError(error);
  const legacyCode = error?.response?.data?.error_code
    || error?.response?.data?.code
    || error?.code;
  const code = typeof structured?.code === 'string'
    ? structured.code.trim()
    : typeof legacyCode === 'string'
      ? legacyCode.trim()
      : '';
  return BLOCKING_PROMPT_RUN_ESTIMATE_ERROR_CODES.has(code);
}
